import { formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Checkbox, Divider, FormControlLabel, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { InfoBanner } from "@src/appV2/redesign/components/InfoBanner/InfoBanner";
import { Text } from "@src/appV2/redesign/components/Text";
import { TimeSlotAvatar } from "@src/appV2/redesign/Shift/TimeSlotAvatar";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";

import { InviteChargeRateRow } from "./InviteChargeRateRow";
import { InviteSelectedProfessional } from "./InviteSelectedProfessional";
import { type Professional } from "./types";

const GRADIENT_BORDER_WIDTH = "1px";

interface InviteShiftCardProps {
  timeSlot: TimeSlot;
  startTime: Date;
  endTime: Date;
  qualificationDisplayName: string;
  timezone: string;
  selectedProfessional?: Professional;
  onRemoveProfessional?: () => void;
  onEditTime: () => void;
  postToMarketplace: boolean;
  onPostToMarketplaceChange: (value: boolean) => void;
  onInfoIconClick: () => void;
  onSendInvite: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  /** Total charge rate (base + boost) to display as primary text */
  chargeRateDisplay?: string;
  /** Base rate for the expandable breakdown row */
  baseChargeRateDisplay?: string;
  /** Boost amount for the expandable breakdown row, only shown when set */
  boostRateDisplay?: string;
  isChargeRateLoading?: boolean;
  onEditChargeRate?: () => void;
}

function formatTimeRange(startTime: Date, endTime: Date, timezone?: string): string {
  const options = timezone ? { timeZone: timezone } : undefined;
  const start = formatTime(startTime, options);
  const end = formatTime(endTime, options);
  return `${start} - ${end}`;
}

/**
 * Shift card component displayed at the bottom of the invite drawer.
 * Shows shift details, selected professional chip, marketplace checkbox, and send button.
 *
 * Per spec:
 * - Checkbox toggles "post to marketplace" option (default: checked)
 * - Info icon opens a modal (not tooltip) explaining the option
 * - Send button disabled until professional selected and validated
 */
export function InviteShiftCard(props: InviteShiftCardProps) {
  const {
    timeSlot,
    startTime,
    endTime,
    qualificationDisplayName,
    timezone,
    selectedProfessional,
    onRemoveProfessional,
    onEditTime,
    postToMarketplace,
    onPostToMarketplaceChange,
    onInfoIconClick,
    onSendInvite,
    isLoading = false,
    isDisabled = false,
    errorMessage,
    chargeRateDisplay,
    baseChargeRateDisplay,
    boostRateDisplay,
    isChargeRateLoading = false,
    onEditChargeRate,
  } = props;

  const formattedTimeRange = formatTimeRange(startTime, endTime, timezone);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPostToMarketplaceChange(event.target.checked);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Bordered card containing shift details and invited section */}
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          borderRadius: theme.borderRadius?.large,
          background: theme.palette.shiftCard?.borderGradient,
          padding: GRADIENT_BORDER_WIDTH,
          boxShadow: "0px 4px 14px -8px rgba(0, 0, 0, 0.35)",
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background?.standout,
            borderRadius: `calc(${theme.borderRadius?.large} - ${GRADIENT_BORDER_WIDTH})`,
            overflow: "hidden",
          })}
        >
          {/* Shift details header */}
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              paddingX: 3,
              paddingY: 4,
              borderBottom: selectedProfessional
                ? `1px solid ${theme.palette.border?.base}`
                : "none",
            })}
          >
            <TimeSlotAvatar timeSlot={timeSlot} />

            <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
              <Text
                semibold
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  fontSize: theme.typography?.body2?.fontSize,
                  lineHeight: "normal",
                })}
                variant="body2"
              >
                {formattedTimeRange}
              </Text>
              <Text
                semibold
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography?.caption?.fontSize,
                  lineHeight: "normal",
                })}
                variant="caption"
              >
                {qualificationDisplayName} shift
              </Text>
            </Stack>

            <IconButton
              aria-label="Edit time"
              iconType="pencil"
              size="small"
              variant="outlined"
              onClick={onEditTime}
            />
          </Box>

          {/* Charge rate row - shown while loading or after rate is available */}
          {isDefined(selectedProfessional) &&
            (isDefined(chargeRateDisplay) || isChargeRateLoading) && (
              <>
                <InviteChargeRateRow
                  baseChargeRateDisplay={baseChargeRateDisplay}
                  boostRateDisplay={boostRateDisplay}
                  chargeRateDisplay={chargeRateDisplay ?? ""}
                  isChargeRateLoading={isChargeRateLoading}
                  onEditChargeRate={onEditChargeRate}
                />
                <Divider />
              </>
            )}

          {/* Selected professional section */}
          {selectedProfessional && (
            <InviteSelectedProfessional
              professional={selectedProfessional}
              onRemove={onRemoveProfessional}
            />
          )}
        </Box>
      </Box>

      {/* Post to marketplace checkbox - outside the card */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={postToMarketplace}
              size="small"
              sx={(theme) => ({
                color: theme.palette.border?.base,
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
              })}
              onChange={handleCheckboxChange}
            />
          }
          label={
            <Text
              sx={(theme) => ({
                color: theme.palette.text.primary,
              })}
              variant="body2"
            >
              If not accepted, post as open shift
            </Text>
          }
          sx={{ flex: 1, margin: 0 }}
        />
        <IconButton
          aria-label="More information"
          iconType="information"
          size="small"
          variant="muted"
          onClick={onInfoIconClick}
        />
      </Box>

      {/* Error message */}
      {errorMessage && (
        <Box>
          <InfoBanner message={errorMessage} variant="error" />
        </Box>
      )}

      {/* Send button - outside the card */}
      <Button
        fullWidth
        disabled={isDisabled || !selectedProfessional}
        size="medium"
        variant="primary"
        onClick={onSendInvite}
      >
        {isLoading ? "Sending..." : "Send Invite"}
      </Button>
    </Box>
  );
}
